/**
 * Aba 4 "Gatilhos" do editor de Playbook (ADR-148 Fatia 3) — sai do
 * honest-defer: o Gestor configura os gatilhos ACIMA do piso.
 *
 * Dupla soberania renderizada com a MESMA `resolveEffectiveTriggers` do
 * runtime (`@lume/shared`):
 *   - **Piso de plataforma** (5): sempre travado (pill "piso de plataforma").
 *   - **Mínimo do tenant**: travado ON com explicação (Admin definiu na Tela 8;
 *     o que o playbook diz é ignorado pelo runtime — desligar aqui seria
 *     affordance falsa).
 *   - **Configuráveis restantes**: toggle por playbook + params editáveis
 *     (`timeoutMinutes` 1-1440 · `maxConsecutiveRejections` 1-10).
 *   - **Detector-pendente** (3): toggle configurável + selo honesto inerte.
 *
 * Mínimos do tenant vêm do `GET /v1/tenant-config` — query GATEADA em
 * `tenant-config-read`; sem a cap, banner honesto avisa que travas do Admin
 * não estão visíveis (mas continuam valendo no runtime).
 */

import {
  PROTECTION_TRIGGER_CATALOG,
  resolveEffectiveTriggers,
  type EffectiveTriggers,
  type ProtectionTriggerDefinition,
  type ProtectionTriggerId,
} from '@lume/shared';
import { Link } from 'react-router-dom';

import { useCapability } from '../../../hooks/use-capability';
import { useTenantConfig } from '../../admin/config-tenant/use-tenant-config';

import type { PlaybookDto } from '../types';
import type { PlaybookForm, TriggersDraft } from '../use-playbook-form';

const HARD_TRIGGERS: readonly ProtectionTriggerDefinition[] = PROTECTION_TRIGGER_CATALOG.filter(
  (t) => t.kind === 'dura',
);
const TRANSITION_TRIGGERS: readonly ProtectionTriggerDefinition[] =
  PROTECTION_TRIGGER_CATALOG.filter((t) => t.kind === 'virada');

/** Mínimos do tenant (Tela 8) — fetch gateado na cap; null = não visível. */
function useTenantMinimums(): { minimums: readonly string[] | null; visible: boolean } {
  const canReadTenantConfig = useCapability('tenant-config-read');
  const query = useTenantConfig(canReadTenantConfig);
  if (!canReadTenantConfig) return { minimums: null, visible: false };
  return { minimums: query.data?.protectionTriggerMinimums ?? [], visible: true };
}

/** Aba em modo leitura (sem `playbook-author`). */
export function TabTriggers({ playbook }: { playbook: PlaybookDto }): JSX.Element {
  const { minimums, visible } = useTenantMinimums();
  const effective = resolveEffectiveTriggers({
    playbookTriggers: playbook.triggers,
    tenantMinimums: minimums,
  });
  return <TriggersPanel effective={effective} minimumsVisible={visible} form={null} draft={null} />;
}

/** Aba em modo edição (com `playbook-author`). */
export function TabTriggersEdit({ form }: { form: PlaybookForm }): JSX.Element {
  const { minimums, visible } = useTenantMinimums();
  // Estado exibido = rascunho do playbook + travas (piso/mínimos) por cima —
  // derivado da MESMA função do runtime para a UI nunca prometer o que o
  // runtime não cumpre.
  const effective = resolveEffectiveTriggers({
    playbookTriggers: {
      version: 1,
      hard: Object.fromEntries(
        HARD_TRIGGERS.filter((t) => !t.platformFloor).map((t) => [
          t.id,
          { enabled: form.draft.triggers.enabled[t.id] ?? t.defaultEnabled },
        ]),
      ),
      transition: Object.fromEntries(
        TRANSITION_TRIGGERS.map((t) => [
          t.id,
          { enabled: form.draft.triggers.enabled[t.id] ?? t.defaultEnabled },
        ]),
      ),
    },
    tenantMinimums: minimums,
  });
  return (
    <TriggersPanel
      effective={effective}
      minimumsVisible={visible}
      form={form}
      draft={form.draft.triggers}
    />
  );
}

function TriggersPanel({
  effective,
  minimumsVisible,
  form,
  draft,
}: {
  effective: EffectiveTriggers;
  minimumsVisible: boolean;
  form: PlaybookForm | null;
  draft: TriggersDraft | null;
}): JSX.Element {
  const editable = form !== null && draft !== null;

  const setEnabled = (id: string, value: boolean): void => {
    if (form === null || draft === null) return;
    form.set('triggers', { ...draft, enabled: { ...draft.enabled, [id]: value } });
  };
  const setParam = (key: 'timeoutMinutes' | 'maxConsecutiveRejections', value: string): void => {
    if (form === null || draft === null) return;
    form.set('triggers', { ...draft, [key]: value });
  };

  return (
    <div className="pb-panel" role="tabpanel" aria-label="Gatilhos">
      <h2 className="pb-panel__title">Gatilhos de Proteção</h2>
      <span className="pb-panel__subtitle">
        Proteções do modo IA-conduz (H_c) — o piso de plataforma e os mínimos do tenant não podem
        ser desligados por playbook.
      </span>

      {!minimumsVisible && (
        <div className="pb-triggers-note" role="note">
          Seu perfil não vê os mínimos do tenant — gatilhos forçados pelo Admin continuam valendo na
          operação mesmo sem aparecer travados aqui.
        </div>
      )}

      <div className="pb-panel__section">
        <span className="pb-panel__section-label">
          Gatilhos de escalação dura ({HARD_TRIGGERS.length})
        </span>
        <div className="pb-triggers-list">
          {HARD_TRIGGERS.map((def) => (
            <TriggerCheck
              key={def.id}
              def={def}
              effective={effective}
              editable={editable}
              onToggle={setEnabled}
            >
              {def.id === 'delegation-timeout' && (
                <ParamInput
                  label="Minutos sem progresso até devolver"
                  placeholder="30 (padrão)"
                  min={1}
                  max={1440}
                  value={draft?.timeoutMinutes ?? ''}
                  editable={editable && effective['delegation-timeout'].enabled}
                  onChange={(v) => {
                    setParam('timeoutMinutes', v);
                  }}
                />
              )}
              {def.id === 'validation-rejected' && (
                <ParamInput
                  label="Reprovações consecutivas até devolver"
                  placeholder="3 (padrão)"
                  min={1}
                  max={10}
                  value={draft?.maxConsecutiveRejections ?? ''}
                  editable={editable && effective['validation-rejected'].enabled}
                  onChange={(v) => {
                    setParam('maxConsecutiveRejections', v);
                  }}
                />
              )}
            </TriggerCheck>
          ))}
        </div>
      </div>

      <div className="pb-panel__divider" />

      <div className="pb-panel__section">
        <span className="pb-panel__section-label">
          Gatilhos de virada de playbook ({TRANSITION_TRIGGERS.length})
        </span>
        <div className="pb-triggers-list">
          {TRANSITION_TRIGGERS.map((def) => (
            <TriggerCheck
              key={def.id}
              def={def}
              effective={effective}
              editable={editable}
              onToggle={setEnabled}
            />
          ))}
        </div>
      </div>

      <div className="pb-panel__divider" />

      <div className="pb-triggers-note">
        <strong>Mínimos não-negociáveis</strong> — gatilhos travados foram definidos pela plataforma
        (piso) ou pelo Admin do tenant na tela de Gatilhos.{' '}
        <Link to="/admin/ia/gatilhos">Ver gatilhos do tenant →</Link>
      </div>
    </div>
  );
}

function TriggerCheck({
  def,
  effective,
  editable,
  onToggle,
  children,
}: {
  def: ProtectionTriggerDefinition;
  effective: EffectiveTriggers;
  editable: boolean;
  onToggle: (id: string, value: boolean) => void;
  children?: React.ReactNode;
}): JSX.Element {
  const eff = effective[def.id as ProtectionTriggerId];
  const locked = eff.locked;
  const checked = eff.enabled;
  const disabled = !editable || locked;
  const lockLabel =
    eff.source === 'platform-floor'
      ? 'piso de plataforma'
      : eff.source === 'tenant-minimum'
        ? 'mínimo do tenant'
        : null;

  return (
    <div className={'pb-trigger-row' + (locked ? ' pb-trigger-row--locked' : '')}>
      <button
        type="button"
        className={'pb-check' + (checked ? ' pb-check--checked' : '')}
        role="switch"
        aria-checked={checked}
        aria-label={def.label}
        disabled={disabled}
        onClick={() => {
          if (!disabled) onToggle(def.id, !checked);
        }}
      >
        <span className="pb-check__box" />
        <span className="pb-check__main">
          <span className="pb-check__label">
            {def.label}
            {lockLabel !== null && <span className="pb-check__lockpill">{lockLabel}</span>}
            {def.signal === 'pending' && (
              <span className="pb-check__pendpill">sem efeito ainda — detector pendente</span>
            )}
          </span>
          <span className="pb-check__desc">{def.description}</span>
        </span>
      </button>
      {children}
    </div>
  );
}

function ParamInput({
  label,
  placeholder,
  min,
  max,
  value,
  editable,
  onChange,
}: {
  label: string;
  placeholder: string;
  min: number;
  max: number;
  value: string;
  editable: boolean;
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <label className="pb-trigger-param">
      <span className="pb-trigger-param__label">{label}</span>
      <input
        type="number"
        className="pb-input pb-trigger-param__input"
        min={min}
        max={max}
        placeholder={placeholder}
        value={value}
        disabled={!editable}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
    </label>
  );
}
